import React, {useCallback, useMemo} from 'react';

import {assert} from 'Asserts';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {
  ScrollableTableColumn,
  ScrollableTableRow,
  TABLE_KEYS,
} from 'ui-library/scrollable/ScrollableTableConstants';

import {MonomerQuery} from '../__generated/MonomerQuery';
import {MONOMER_QUERY} from '../MonomerConstants';
import {APIMonomer, createMonomers} from '../MonomersDAO';
import {CoreLibMonomer, HELMCoreMonomerLibraryAPI} from '../MonomersUtil';
import SmilesChemicalStructure from '../SmilesChemicalStructure';
import SharedHELMMonomerSelectionPage from './SharedHELMMonomerSelectionPage';

interface HelmLibraryMonomerRowSortableData {
  name: string;
  symbol: string;
  naturalAnalogue: string;
  monomerType: string;
  smiles: string;
  chemicalStructureImage: JSX.Element;
}

export interface HelmLibraryMonomerRow extends ScrollableTableRow {
  data: HelmLibraryMonomerRowSortableData;
}

interface HelmLibraryMonomerColumn extends ScrollableTableColumn {
  sortKey?: string;
}

const HELM_LIBRARY_TABLE_COLUMNS: Array<HelmLibraryMonomerColumn> = [
  {
    header: 'Name',
    accessorKey: 'name',
    flexGrow: 1,
    isCollapsible: false,
    isSortable: true,
    sortKey: 'name',
  },
  {
    header: 'Symbol',
    accessorKey: 'symbol',
    isCollapsible: true,
    isSortable: true,
    sortKey: 'symbol',
  },
  {
    header: 'Chemical structure',
    accessorKey: 'chemicalStructureImage',
    flexGrow: 1,
    isCollapsible: true,
    isSortable: false,
  },
  {
    header: 'Natural analogue',
    accessorKey: 'naturalAnalogue',
    isCollapsible: true,
    isSortable: true,
    sortKey: 'naturalAnalogue',
  },
  {
    header: 'Monomer type',
    accessorKey: 'monomerType',
    isCollapsible: true,
    isSortable: true,
    sortKey: 'monomerType',
  },
  {
    header: 'SMILES',
    accessorKey: 'smiles',
    flexGrow: 1,
    isCollapsible: true,
    isSortable: true,
    sortKey: 'smiles',
    wrapText: true,
  },
];

function getMonomerKey(monomer: {monomerType: string; symbol: string}): string {
  return `${monomer.monomerType.toLowerCase()}-${monomer.symbol}`;
}

function mapMonomerToRowData(
  monomer: CoreLibMonomer,
  classes: Record<string, string>
): HelmLibraryMonomerRowSortableData {
  return {
    name: monomer.name,
    symbol: monomer.symbol,
    naturalAnalogue: monomer.naturalAnalog,
    monomerType: monomer.monomerType,
    smiles: monomer.smiles,
    chemicalStructureImage: (
      <SmilesChemicalStructure
        smiles={monomer.smiles}
        className={classes.chemicalStructureContainer}
        svgClassName={classes.chemicalStructureSvg}
      />
    ),
  };
}

const EMPTY_SET = new Set<string>();

export interface MonomerLibraryHELMPageProps {
  viewOnly?: boolean;
}

export default function MonomerLibraryHELMPage({viewOnly = false}: MonomerLibraryHELMPageProps): JSX.Element {
  const queryResult = useBenchlingQuery<MonomerQuery>(MONOMER_QUERY, {
    renderCachedDataWhenRefetching: true,
  });

  const existingMonomerKeys = useMemo(() => {
    if (queryResult.type !== QueryResultType.SUCCESS) {
      return EMPTY_SET;
    }
    return new Set(queryResult.data.monomers.map((mon) => getMonomerKey(mon)));
  }, [queryResult]);

  const refetch = queryResult.refetch;

  const fetchHelmMonomers = useCallback(async () => {
    const monomers = await HELMCoreMonomerLibraryAPI.fetch();
    return monomers.filter((mon) => mon.polymerType === 'RNA');
  }, []);

  const handleSubmit = useCallback(
    async (selectedKeys: Array<string>, helmMonomers: Array<CoreLibMonomer>): Promise<number> => {
      const newMonomers: Array<APIMonomer> = selectedKeys.map((selectedMonomerInfo) => {
        const coreLibMonomer = helmMonomers.find((monomer) => getMonomerKey(monomer) === selectedMonomerInfo);
        assert(coreLibMonomer != null, `Could not find HELM library monomer for ${selectedMonomerInfo}`);
        const {name, symbol, naturalAnalog, smiles} = coreLibMonomer;
        return {name, symbol, naturalAnalogue: naturalAnalog, smiles};
      });
      const result = await createMonomers(newMonomers);
      return result.length;
    },
    []
  );

  return (
    <SharedHELMMonomerSelectionPage<HelmLibraryMonomerRow>
      monomerDescriptor="monomer"
      libraryUrl="/monomers"
      changeMethodUrl="/monomers/create"
      fetchHelmMonomers={fetchHelmMonomers}
      existingMonomerKeys={existingMonomerKeys}
      isExistingDataLoading={queryResult.type === QueryResultType.PENDING}
      isExistingDataError={queryResult.type === QueryResultType.ERROR}
      refetch={refetch}
      getMonomerKey={getMonomerKey}
      columns={HELM_LIBRARY_TABLE_COLUMNS}
      mapMonomerToRowData={mapMonomerToRowData}
      tableKey={TABLE_KEYS.MONOMER_LISTING_TABLE}
      onSubmit={handleSubmit}
      viewOnly={viewOnly}
      dataTestAttrs={{component: 'MonomerLibraryHELMPage', element: 'root'}}
    />
  );
}
